import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Divider
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import HelpIcon from '@mui/icons-material/Help';
import AltRouteIcon from '@mui/icons-material/AltRoute';
import AssignmentIcon from '@mui/icons-material/Assignment';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import UndoIcon from '@mui/icons-material/Undo';
import favicon from '../assets/Fosterway_favicon.png';

interface CustomEligibilityIndicatorsModalProps {
  open: boolean;
  onClose: () => void;
}

interface EligibilityIndicator {
  icon: React.ReactNode;
  description: string;
}

const CustomEligibilityIndicatorsModal: React.FC<CustomEligibilityIndicatorsModalProps> = ({ open, onClose }) => {
  const indicators: EligibilityIndicator[] = [
    {
      icon: (
        <CheckCircleIcon sx={{ color: '#4caf50', fontSize: 96 }} />
      ),
      description: "You are eligible for this program based on your overall average, combined with your courses and grades meeting or exceeding the minimum requirements for this program. This is typically shown for Grade 11 and 12 students or students who have entered projected Grade 11/12 courses and grades."
    },
    {
      icon: (
        <Box sx={{
          width: 96,
          height: 96,
          borderRadius: '50%',
          backgroundColor: '#8dd439',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <ArrowUpwardIcon sx={{ color: 'white', fontSize: 60 }} />
        </Box>
      ),
      description: "You are on the right path for this program based on the courses and grades you have provided. This is typically shown for Grade 10 students and verifies the current courses and grades put you on a path to take the prerequisite courses for the program being evaluated."
    },
    {
      icon: (
        <Box sx={{
          width: 96,
          height: 96,
          borderRadius: '50%',
          backgroundColor: '#8dd439',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <AssignmentIcon sx={{ color: 'white', fontSize: 60 }} />
        </Box>
      ),
      description: "Prerequisites are not provided for this program. There may be general prerequisites or no prerequisites published. Please review the school's description to determine how your situation applies."
    },
    {
      icon: (
        <Box sx={{
          width: 96,
          height: 96,
          borderRadius: '50%',
          backgroundColor: '#ffc107',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <ArrowUpwardIcon sx={{ color: 'white', fontSize: 60 }} />
        </Box>
      ),
      description: "You are on the right path for this program based on the courses you have provided, but your grades need to improve from where they are when you take the prerequisite course for this program. This is typically shown for Grade 10 students and verifies the current courses and grades put you on a path to take the prerequisite courses for the program being evaluated."
    },
    {
      icon: (
        <Box sx={{
          width: 96,
          height: 96,
          borderRadius: '50%',
          backgroundColor: '#1976d2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <AltRouteIcon sx={{ color: 'white', fontSize: 60 }} />
        </Box>
      ),
      description: "Not yet eligible - check eligibility for specific requirements, or your average may be below the program minimum requirements. This is typically shown to Grade 11 and 12 students and shows you are not yet eligible according to the published requirements and grades need to be improved or additional courses need to be completed to meet all of the program requirements."
    },
    {
      icon: (
        <Box sx={{
          width: 96,
          height: 96,
          borderRadius: '50%',
          backgroundColor: '#1976d2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center'
        }}>
          <UndoIcon sx={{ color: 'white', fontSize: 60 }} />
        </Box>
      ),
      description: "Missing prerequisites - you need to complete required courses. To be eligible for this program, you likely need to revisit your course schedule with your guidance counselor to ensure you are on a pathway to complete prerequisite courses."
    },
    {
      icon: (
        <HelpIcon sx={{ color: '#bdbdbd', fontSize: 96 }} />
      ),
      description: "Calculation is in process or eligibility was not checked. You can initiate a re-calculation of eligibility for all 6,500+ programs by adding, removing, or updating a course or grade."
    }
  ];

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        pb: 2
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <img 
            src={favicon} 
            alt="Fosterway Favicon" 
            style={{ height: 24, width: 24 }}
          />
          <Typography variant="h6">
            Custom Eligibility Indicators
          </Typography>
        </Box>
        <Button
          onClick={onClose}
          sx={{ minWidth: 'auto', p: 0.5 }}
        >
          <CloseIcon />
        </Button>
      </DialogTitle>
      <DialogContent sx={{ px: 3, py: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Introductory Paragraphs */}
          <Typography variant="body1" sx={{ mb: 0.4, width: '100%' }}>
            The Fosterway Custom Eligibility Calculator provides an eligibility indicator for over 6,500 programs across 150 colleges and universities throughout Canada. Program eligibility is determined through a detailed review of the published prerequisite course requirements, minimum grades and detailed, program-specific, average calculations.
          </Typography>
          
          <Typography variant="body1" sx={{ mb: 2, width: '100%' }}>
            At least 6 courses and grades are required to provide an eligibility indicator, and the more information provided, the more accurate the eligibility indicator will be. We encourage you to enter projected courses and grades to help ensure your planned pathway will provide the results you're looking for.
          </Typography>
          
          <Divider sx={{ mb: 2 }} />
          
          {indicators.map((indicator, index) => (
            <Box key={index}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Box sx={{ flexShrink: 0 }}>
                  {indicator.icon}
                </Box>
                <Typography variant="body1" sx={{ flex: 1 }}>
                  {indicator.description}
                </Typography>
              </Box>
              {index < indicators.length - 1 && (
                <Divider sx={{ mt: 2 }} />
              )}
            </Box>
          ))}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button 
          onClick={onClose} 
          variant="contained"
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CustomEligibilityIndicatorsModal;

